import { ICollectionSmartContract } from "@novel/shared/interfaces/CollectionSmartContract";
import { ICryptoAddress } from "@novel/shared/interfaces/CryptoAddress";
import { ICryptoWallet } from "@novel/shared/interfaces/CryptoWallet";
import { retryWithBackoff } from "@novel/shared/utils/retryWithBackoff";
import { getIsTestnet } from "@novel/shared/utils/cryptoLinkUtils";
import { SafePromise } from "@novel/shared/utils/SafePromise";
import { SurfaceableError } from "@novel/shared/utils/SurfaceableError";
import { IAuthWallet, AuthWallet } from "@novel/shared/utils/wallets/AuthWallet";
import {
    getCachedGsnProvider,
    execWeb3SendWithGsn,
} from "@novel/shared/utils/wallets/execWeb3SendWithGsn";
import { NETWORK_MAP, NETWORK_MAP_BY_CHAIN_ID } from "@novel/shared/utils/wallets/networkMap";
import {
    getWalletClient,
    getTorusInstance,
    clearWalletConnected,
} from "@novel/shared/utils/wallets/walletLibInstances";
import Web3 from "web3";
import { getNovelCollectionArtifactAbi } from "@novel/shared/utils/wallets/getNovelCollectionArtifactAbi";
import { shortenCryptoAddress } from "@novel/shared/utils/shortenCryptoAddress";
import { blockchainAddressEquals } from "@novel/shared/utils/blockchainHashEquals";
import type { RestReq } from "@novel/shared/utils/restReq";

export interface IMintingWallet extends IAuthWallet {
    wallet: ICryptoWallet;

    whitelistClaim: (
        whitelistClaimArgs: WhitelistClaimArgs,
        wallet?: ICryptoWallet,
    ) => Promise<void>;
    isClaimIdAlreadyMinted: (
        cryptoAddress: ICryptoAddress,
        collectionSmartContract: ICollectionSmartContract,
        claimId: string,
        wallet?: ICryptoWallet,
    ) => Promise<boolean>;
    mintToken: (
        cryptoAddress: ICryptoAddress,
        collectionSmartContract: ICollectionSmartContract,
        quantity: number,
        claimId: string,
        onTransactionHash?: (transactionHash: string) => void,
    ) => Promise<{ transactionHash: string }>;
}

interface WhitelistClaimArgs {
    orderId: string;
    claimId: string;
    cryptoAddress: string;
    claimQuantity: number;
    collectionSmartContract: ICollectionSmartContract;
}

export class MintingWallet extends AuthWallet implements IMintingWallet {
    wallet: ICryptoWallet;

    requestMaker: RestReq;

    constructor(
        requestMaker: RestReq,
        wallet: ICryptoWallet,
        preMobileAppRedirectHook?: () => string,
    ) {
        super(wallet, preMobileAppRedirectHook);
        this.wallet = wallet;
        this.requestMaker = requestMaker;
    }

    private async getMintablePolygonWeb3Instance(
        collectionSmartContract: ICollectionSmartContract,
    ): Promise<Web3> {
        await this.ensureMintableWalletState(collectionSmartContract);

        if (this.walletProvider === "phantom") {
            throw new SurfaceableError("Phantom currently not supported!");
        }
        const { web3Instance } = await getWalletClient(this.walletProvider);

        return web3Instance;
    }

    // wallet must be connected and on polygon network to mint
    private async ensureMintableWalletState(
        collectionSmartContract: ICollectionSmartContract,
    ): Promise<void> {
        await this.ensureClientSideWalletConnection();

        const isTestnet = getIsTestnet(collectionSmartContract.blockchain);
        if (isTestnet) {
            await this.setChainId(NETWORK_MAP.MUMBAI.CHAIN_ID_DECIMAL);
        } else {
            await this.setChainId(NETWORK_MAP.MATIC.CHAIN_ID_DECIMAL);
        }
    }

    private async setChainId(newChainId: number): Promise<void> {
        if (this.walletProvider === "wallet-connect-v2") {
            return;
        }
        if (this.walletProvider === "phantom") {
            throw new SurfaceableError("Phantom currently not supported!");
        }

        const networkMapLookup = NETWORK_MAP_BY_CHAIN_ID[newChainId];

        if (!networkMapLookup) {
            throw new SurfaceableError(`Chain ID ${newChainId} network mapping not configured`);
        }

        const { provider, web3Instance } = await getWalletClient(this.walletProvider);
        const currentChainId = Number(await web3Instance.eth.getChainId());

        if (currentChainId !== newChainId) {
            if (this.walletProvider === "torus") {
                /// switching in torus is unfortunately inconsistent
                const walletInstance = await getTorusInstance();
                await walletInstance.client.setProvider({
                    host: networkMapLookup.HOST, // default: mainnet
                    chainId: networkMapLookup.CHAIN_ID_DECIMAL, // default: 1
                    networkName: networkMapLookup.NETWORK_CONFIG.chainName, // default: Main Ethereum Network
                });
                walletInstance.web3Instance = new Web3(walletInstance.client.provider as any);
            } else {
                try {
                    await provider.request({
                        method: "wallet_switchEthereumChain",
                        params: [{ chainId: networkMapLookup.NETWORK_CONFIG.chainId }],
                    });
                } catch (switchError: any) {
                    // 4902 indicates that the client does not recognize chainId network (32603 is mystery error)
                    // wallet connect does not provide the code so we must fall back with string match
                    const NEED_TO_ADD_ERROR_STR = "Unrecognized chain ID";
                    if (
                        switchError.code === 4902 ||
                        switchError.code === -32603 ||
                        switchError?.message?.includes(NEED_TO_ADD_ERROR_STR)
                    ) {
                        try {
                            await provider.request({
                                method: "wallet_addEthereumChain",
                                params: [networkMapLookup.NETWORK_CONFIG],
                            });
                        } catch (err: any) {
                            throw new SurfaceableError("Adding network is rejected");
                        }
                    } else {
                        throw new SurfaceableError("Switching network is rejected");
                    }
                }
            }
        }
    }

    async whitelistClaim(whitelistClaimArgs: WhitelistClaimArgs): Promise<void> {
        const isWhitelistedInFrontendRpc = (provider: Web3) =>
            this.isClaimIdAlreadyWhitelisted(
                whitelistClaimArgs.cryptoAddress,
                whitelistClaimArgs.collectionSmartContract,
                whitelistClaimArgs.claimId,
                whitelistClaimArgs.claimQuantity,
                provider,
            );

        const web3Instance = await this.getMintablePolygonWeb3Instance(
            whitelistClaimArgs.collectionSmartContract,
        );

        const getGsnProvider: () => Promise<Web3> = () =>
            getCachedGsnProvider(web3Instance, whitelistClaimArgs.collectionSmartContract);

        const gnsprovider = await getGsnProvider();

        if (
            (await isWhitelistedInFrontendRpc(web3Instance)) &&
            (await isWhitelistedInFrontendRpc(gnsprovider))
        ) {
            return;
        }

        await this.whitelistTokensForClaimApiCall(whitelistClaimArgs);

        const isWhitelistComplete = async () =>
            Promise.all([
                isWhitelistedInFrontendRpc(web3Instance),
                isWhitelistedInFrontendRpc(gnsprovider),
            ]).then((result) => result.every(Boolean));

        // eslint-disable-next-line no-constant-condition
        while (true) {
            // poll until front end RPC catches up with back end RPC
            if (await isWhitelistComplete()) {
                break;
            }
        }
    }

    private async whitelistTokensForClaimApiCall(
        whitelistClaimArgs: WhitelistClaimArgs,
    ): Promise<void> {
        const _whitelistTokensForClaimApiCall = async ({
            orderId,
            claimId,
            cryptoAddress,
            claimQuantity,
            collectionSmartContract,
        }: WhitelistClaimArgs) => {
            const res = await this.requestMaker.postReq("/api/customer/whitelist", {
                reqBody: {
                    orderId,
                    claimId,
                    collectionContractAddress: collectionSmartContract.address,
                    quantity: claimQuantity,
                    cryptoAddress,
                },
            });

            if (res.type === "error") {
                throw res.error;
            }
        };

        return retryWithBackoff(() => _whitelistTokensForClaimApiCall(whitelistClaimArgs));
    }

    private async isClaimIdAlreadyWhitelisted(
        cryptoAddress: string,
        collectionSmartContract: ICollectionSmartContract,
        claimId: string,
        quantity: number,
        provider: Web3,
    ): Promise<boolean> {
        const isTestnet = getIsTestnet(collectionSmartContract.blockchain);
        const abiPromise = getNovelCollectionArtifactAbi(isTestnet);

        const contractInstance = new provider.eth.Contract(
            (await abiPromise) as any,
            collectionSmartContract.address,
        );
        const whitelistedClaimIdAmountPromise = new SafePromise(
            retryWithBackoff<string>(() =>
                contractInstance.methods.whitelistedClaimIdAmount(claimId).call({
                    from: cryptoAddress,
                }),
            ).then((v) => (v ? +v : 0)),
        );

        const whitelistedClaimIdForAddressPromise = new SafePromise(
            retryWithBackoff<boolean>(() =>
                contractInstance.methods.whitelistedClaimIdForAddress(cryptoAddress, claimId).call({
                    from: cryptoAddress,
                }),
            ),
        );

        const mintedClaimIdAmountPromise = new SafePromise(
            retryWithBackoff<string>(() =>
                contractInstance.methods.mintedClaimIdAmount(claimId).call({
                    from: cryptoAddress,
                }),
            ).then((v) => (v ? +v : 0)),
        );
        const canMintAmountForAddressPromise = new SafePromise(
            retryWithBackoff<boolean>(() =>
                contractInstance.methods.canMintAmount(cryptoAddress, quantity).call({
                    from: cryptoAddress,
                }),
            ),
        );

        const whitelistedClaimIdAmount = await whitelistedClaimIdAmountPromise;
        if (!whitelistedClaimIdAmount) {
            return false;
        }

        const whitelistedClaimIdForAddress = await whitelistedClaimIdForAddressPromise;
        if (!whitelistedClaimIdForAddress) {
            return false;
        }

        const mintedClaimIdAmount = await mintedClaimIdAmountPromise;
        if (mintedClaimIdAmount) {
            return true;
        }

        const canMintAmount = await canMintAmountForAddressPromise;
        return canMintAmount;
    }

    async isClaimIdAlreadyMinted(
        cryptoAddress: ICryptoAddress,
        collectionSmartContract: ICollectionSmartContract,
        claimId: string,
    ): Promise<boolean> {
        const web3Instance = await this.getMintablePolygonWeb3Instance(collectionSmartContract);
        const isTestnet = getIsTestnet(collectionSmartContract.blockchain);

        const contractInstance = new web3Instance.eth.Contract(
            (await getNovelCollectionArtifactAbi(isTestnet)) as any,
            collectionSmartContract.address,
        );

        const mintedClaimIdAmountRes = +(await retryWithBackoff<string>(() =>
            contractInstance.methods.mintedClaimIdAmount(claimId).call({
                from: cryptoAddress.address,
            }),
        ));

        return !!mintedClaimIdAmountRes;
    }

    private async ensureAddressIsAuthorized(
        cryptoAddress: ICryptoAddress,
        collectionSmartContract: ICollectionSmartContract,
    ) {
        const web3Instance = await this.getMintablePolygonWeb3Instance(collectionSmartContract);

        const addresses = (await web3Instance.eth.getAccounts()).map((address) =>
            address.toLowerCase(),
        );

        if (!addresses.some((address) => blockchainAddressEquals(address, cryptoAddress.address))) {
            if (this.walletProvider === "wallet-connect") {
                const { provider } = await getWalletClient(this.walletProvider);
                await clearWalletConnected(provider);
            }
            if (this.walletProvider === "torus") {
                const { client } = await getWalletClient(this.walletProvider);
                await client.logout();
            }

            if (this.walletProvider !== "metamask") {
                throw new SurfaceableError(
                    `The wallet you are attempting to sign with has not been authorized for this address ${shortenCryptoAddress(
                        cryptoAddress.address,
                    )}.`,
                );
            }

            // metamask has multiple wallets and there is no way for us to read
            // the ones not presently connected to the browser, therefore we throw
            // a special instructive error for metamask users
            throw new SurfaceableError(
                `Switch your selected Metamask account address to ${shortenCryptoAddress(
                    cryptoAddress.address,
                )} in order to sign with this address.`,
            );
        }
    }

    async mintToken(
        cryptoAddress: ICryptoAddress,
        collectionSmartContract: ICollectionSmartContract,
        quantity: number,
        claimId: string,
        onTransactionHash?: (transactionHash: string) => void,
    ): Promise<{ transactionHash: string }> {
        const isTestnet = getIsTestnet(collectionSmartContract.blockchain);

        const web3Instance = await this.getMintablePolygonWeb3Instance(collectionSmartContract);

        const populateGsnProviderCachePromise = new SafePromise(
            getCachedGsnProvider(web3Instance, collectionSmartContract),
        );

        const contractInstance = new web3Instance.eth.Contract(
            (await getNovelCollectionArtifactAbi(isTestnet)) as any,
            collectionSmartContract.address,
        );

        const mintableAmountPromise = new SafePromise(
            retryWithBackoff<string>(() =>
                contractInstance.methods.mintableAmount(cryptoAddress.address).call({
                    from: cryptoAddress.address,
                }),
            ),
        );

        await this.ensureAddressIsAuthorized(cryptoAddress, collectionSmartContract);

        const mintableAmount = +(await mintableAmountPromise);
        if (mintableAmount < quantity) {
            throw new SurfaceableError(
                `Whitelisted token balance for collection is insufficient, your balance: ${mintableAmount}`,
            );
        }

        let transactionHash = "";
        try {
            await populateGsnProviderCachePromise;
            await execWeb3SendWithGsn(web3Instance, collectionSmartContract, {
                onTransactionHash: (_transactionHash) => {
                    const casedTransactionHash = _transactionHash.toLowerCase();
                    if (!transactionHash) {
                        transactionHash = casedTransactionHash;
                    }
                    if (onTransactionHash) {
                        return onTransactionHash(casedTransactionHash);
                    }
                },
                cryptoWalletAddress: cryptoAddress.address,
                methodWithArgsSupplied: contractInstance.methods.mint(quantity, claimId),
            });
        } catch (err) {
            if (!transactionHash) {
                throw err;
            }
        }

        return { transactionHash };
    }
}
